export interface Bank {
  code: string;
  name: string;
}

export const banks: Bank[] = [
  { code: "002", name: "KDB산업은행" },
  { code: "003", name: "IBK기업은행" },
  { code: "004", name: "KB국민은행" },
  { code: "007", name: "Sh수협은행" },
  { code: "011", name: "NH농협은행" },
  { code: "012", name: "지역 농·축협" },
  { code: "020", name: "우리은행" },
  { code: "023", name: "SC제일은행" },
  { code: "027", name: "한국씨티은행" },
  { code: "031", name: "iM뱅크" },
  { code: "032", name: "BNK부산은행" },
  { code: "034", name: "광주은행" },
  { code: "035", name: "제주은행" },
  { code: "037", name: "전북은행" },
  { code: "039", name: "BNK경남은행" },
  { code: "045", name: "새마을금고" },
  { code: "048", name: "신협" },
  { code: "050", name: "저축은행" },
  { code: "054", name: "HSBC은행" },
  { code: "064", name: "산림조합중앙회" },
  { code: "071", name: "우체국" },
  { code: "081", name: "하나은행" },
  { code: "088", name: "신한은행" },
  { code: "089", name: "케이뱅크" },
  { code: "090", name: "카카오뱅크" },
  { code: "092", name: "토스뱅크" }
];

export const bankName = (code: string) => banks.find((bank) => bank.code === code)?.name ?? code;

export const findBank = (value: string) => {
  const normalized = value.trim().toLocaleLowerCase("ko-KR");
  return banks.find((bank) =>
    bank.code === normalized ||
    bank.name.toLocaleLowerCase("ko-KR") === normalized ||
    `${bank.name} (${bank.code})`.toLocaleLowerCase("ko-KR") === normalized
  );
};

/**
 * 은행별 계좌번호 끊는 자리. 공통 규칙이 없어 은행마다 적어 둔다 — 임의로 네 자리씩 끊으면
 * 통장과 다른 모양이 되어 맞는지 대조할 수가 없다.
 *
 * 한 은행이 자릿수가 여럿인 경우(구 계좌·신 계좌)는 길이별로 나눠 적는다. 표에 없는 은행과
 * 표에 없는 길이는 손으로 친 그대로 둔다 — 틀린 자리에 끊는 것보다 안 끊는 편이 낫다.
 */
const accountFormats: Record<string, Record<number, number[]>> = {
  "004": { 14: [6, 2, 6], 12: [6, 2, 4] },      // KB국민
  "088": { 12: [3, 3, 6], 11: [3, 2, 6] },      // 신한
  "020": { 13: [4, 3, 6] },                     // 우리
  "081": { 14: [3, 6, 5] },                     // 하나
  "011": { 13: [3, 4, 4, 2] },                  // NH농협
  "012": { 13: [3, 4, 4, 2] },                  // 지역 농·축협
  "003": { 14: [3, 6, 2, 3] },                  // IBK기업
  "090": { 13: [4, 2, 7] },                     // 카카오뱅크
  "092": { 12: [4, 4, 4] },                     // 토스뱅크
  "089": { 12: [3, 3, 6] },                     // 케이뱅크
  "007": { 13: [3, 4, 4, 2] },                  // Sh수협
  "071": { 13: [6, 2, 5] },                     // 우체국
  "031": { 13: [3, 2, 6, 2] },                  // iM뱅크
  "032": { 13: [3, 4, 4, 2] },                  // BNK부산
  "039": { 13: [3, 4, 4, 2] },                  // BNK경남
};

/**
 * 숫자만 남겨 그 은행의 자리대로 끊는다. 다 치기 전에도 끊는다 — 마지막 자리를 눌러야
 * 하이픈이 나타나면 치는 동안에는 통장과 대조할 수가 없다. 그래서 지금 길이가 향하고 있는
 * 자릿수(그 은행의 길이 중 현재 길이 이상인 가장 짧은 것)의 규칙을 미리 적용한다.
 * 모르는 은행이거나 아는 길이를 넘어서면 숫자만 돌려준다.
 */
export const formatAccountNumber = (value: string, bankCode: string) => {
  const lengths = Object.keys(accountFormats[bankCode] ?? {}).map(Number).sort((a, b) => a - b);
  /* 아는 은행이면 그 은행의 가장 긴 자릿수에서 끊는다. 계속 쳐지면 한 자리 더 쳤는지를
     눈으로 세야 하고, 넘긴 숫자는 하이픈도 못 받아 틀린 줄도 모른다. */
  const digits = value.replace(/[^0-9]/g, "").slice(0, lengths.length ? lengths[lengths.length - 1] : 20);
  const target = lengths.find((length) => length >= digits.length);
  const groups = target ? accountFormats[bankCode][target] : undefined;
  if (!groups || !digits) return digits;
  const parts: string[] = [];
  let index = 0;
  for (const size of groups) { parts.push(digits.slice(index, index + size)); index += size; }
  return parts.filter(Boolean).join("-");
};
